import { APP_RELEASE, type Release, RELEASES, releasesSince } from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { formatDate } from '../lib/format.js';

const LAST_SEEN_KEY = 'k-management.last-seen-release';
const MUTED_KEY = 'k-management.hide-release-notes';

/** Reading a browser storage can fail (private windows, blocked cookies). */
function readSetting(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSetting(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Without storage the notes come back at the next visit: acceptable.
  }
}

/**
 * The version in the corner of every page (ADR-019). After an update it opens
 * by itself with what changed; clicking it shows the whole history.
 */
export function ReleaseNotes() {
  const [open, setOpen] = useState<'news' | 'history' | null>(null);
  const [news, setNews] = useState<readonly Release[]>([]);
  const [muted, setMuted] = useState(() => readSetting(MUTED_KEY) === 'true');

  useEffect(() => {
    const lastSeen = readSetting(LAST_SEEN_KEY);
    // First visit: only the latest release, not the whole history.
    const unseen = lastSeen ? releasesSince(lastSeen) : RELEASES.slice(0, 1);
    writeSetting(LAST_SEEN_KEY, APP_RELEASE);
    if (unseen.length === 0 || readSetting(MUTED_KEY) === 'true') return;
    setNews(unseen);
    setOpen('news');
  }, []);

  const mute = (value: boolean) => {
    setMuted(value);
    writeSetting(MUTED_KEY, String(value));
  };

  return (
    <>
      <button type="button" className="link" onClick={() => setOpen('history')}>
        K-Management {APP_RELEASE}
      </button>
      {open === 'news' && (
        <ReleaseDialog
          title={news.length === 1 ? `Novità della versione ${news[0]!.version}` : 'Novità'}
          releases={news}
          onClose={() => setOpen(null)}
        >
          <label className="checkbox">
            <input type="checkbox" checked={muted} onChange={(e) => mute(e.target.checked)} />
            Non mostrare più le novità all’avvio
          </label>
        </ReleaseDialog>
      )}
      {open === 'history' && (
        <ReleaseDialog
          title="Versioni di K-Management"
          releases={RELEASES}
          onClose={() => setOpen(null)}
        >
          <label className="checkbox">
            <input type="checkbox" checked={!muted} onChange={(e) => mute(!e.target.checked)} />
            Mostrami le novità quando esce una nuova versione
          </label>
        </ReleaseDialog>
      )}
    </>
  );
}

function ReleaseDialog({
  title,
  releases,
  children,
  onClose,
}: {
  title: string;
  releases: readonly Release[];
  children: React.ReactNode;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  return (
    <dialog ref={dialogRef} className="dialog release-dialog" onClose={onClose}>
      <div className="stack">
        <h2>{title}</h2>
        <div className="stack release-list">
          {releases.map((release) => (
            <section key={release.version} className="stack">
              {/* With a single release its number is already in the title. */}
              {releases.length > 1 && <h3>Versione {release.version}</h3>}
              <small className="muted">Pubblicata il {formatDate(release.date)}</small>
              <ul className="plain-list">
                {release.changes.map((change) => (
                  <li key={change}>{change}</li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        {children}
        <div className="actions">
          <button type="button" className="primary" onClick={onClose}>
            Chiudi
          </button>
        </div>
      </div>
    </dialog>
  );
}
